/**
 * Topic ID to Content Page Slug Mapping
 * 
 * Maps old topic IDs from practice pages to new ContentPage slugs
 */

export const topicIdToSlugMap: Record<string, string> = {
  // Node.js topics
  'intro-1': 'nodejs-what-is-nodejs',
  'intro-2': 'nodejs-vs-browser-js',
  'intro-3': 'nodejs-installing',
  'modules': 'nodejs-modules',
  'npm': 'nodejs-npm',
  'file-system': 'nodejs-file-system',
  'path-module': 'nodejs-path-module',
  'events': 'nodejs-events',
  'http-basics': 'nodejs-http-basics',
  'express-intro': 'nodejs-express-intro',
  'routing': 'nodejs-routing',
  'middleware': 'nodejs-middleware',
  'error-handling': 'nodejs-error-handling',
  'callbacks': 'nodejs-callbacks',
  'promises-node': 'nodejs-promises',
  'async-await-node': 'nodejs-async-await',
  'streams': 'nodejs-streams',
  'mongodb': 'nodejs-mongodb',
  'postgresql': 'nodejs-postgresql',
  'redis': 'nodejs-redis',

  // Docker topics (note: Docker uses same IDs as Node.js, so we need to handle in getSlugFromTopicId)
  // These are handled dynamically in getSlugFromTopicId function

  // Databases topics (note: some IDs overlap with Node.js, handled dynamically)
  // mongodb, redis overlap with Node.js - handled in getSlugFromTopicId
};

/**
 * Get content page slug from topic ID
 */
export function getSlugFromTopicId(topicId: string, topicType: 'nodejs' | 'docker' | 'databases' | 'system-design' | 'lld' | 'hld'): string | null {
  // Node.js topics - direct mapping
  if (topicType === 'nodejs' && topicIdToSlugMap[topicId]) {
    return topicIdToSlugMap[topicId];
  }

  // Docker topics - prefix with 'docker-'
  if (topicType === 'docker') {
    const dockerSlugMap: Record<string, string> = {
      'intro-1': 'docker-what-is-docker',
      'intro-2': 'docker-containers-vs-vms',
      'intro-3': 'docker-architecture',
      'installation': 'docker-installation',
      'dockerfile': 'docker-dockerfile-basics',
      'images': 'docker-images',
      'containers': 'docker-containers',
      'docker-compose': 'docker-compose',
      'networking': 'docker-networking',
      'volumes': 'docker-volumes',
      'multi-stage': 'docker-multi-stage-builds',
      'optimization': 'docker-image-optimization',
    };
    return dockerSlugMap[topicId] || null;
  }

  // Databases topics - prefix with 'databases-'
  if (topicType === 'databases') {
    const dbSlugMap: Record<string, string> = {
      'intro-1': 'databases-what-is-database',
      'intro-2': 'databases-sql-vs-nosql',
      'intro-3': 'databases-design-principles',
      'sql-basics': 'databases-sql-basics',
      'queries': 'databases-select-queries',
      'joins': 'databases-joins',
      'aggregations': 'databases-aggregations',
      'subqueries': 'databases-subqueries',
      'normalization': 'databases-normalization',
      'relationships': 'databases-relationships',
      'indexes': 'databases-indexes',
      'transactions': 'databases-transactions',
      'mongodb': 'databases-mongodb',
      'redis': 'databases-redis',
      'cassandra': 'databases-cassandra',
    };
    return dbSlugMap[topicId] || null;
  }

  // System Design topics - slug matches topic ID directly (as seeded)
  if (topicType === 'system-design') {
    // For system design, the slug is the same as the topic ID
    // e.g., 'intro-1' -> 'intro-1', 'scalability' -> 'scalability'
    return topicId;
  }

  // LLD topics - slug is prefixed with 'lld-' to avoid conflicts with System Design
  if (topicType === 'lld') {
    // For LLD, prefix the topic ID with 'lld-'
    // e.g., 'intro-1' -> 'lld-intro-1', 'oop-4' -> 'lld-oop-4', 'factory-method' -> 'lld-factory-method'
    return `lld-${topicId}`;
  }

  // HLD topics - slug is prefixed with 'hld-' to avoid conflicts with System Design
  if (topicType === 'hld') {
    // For HLD, prefix the topic ID with 'hld-'
    // e.g., 'intro-1' -> 'hld-intro-1', 'req-1' -> 'hld-req-1', 'est-1' -> 'hld-est-1'
    return `hld-${topicId}`;
  }

  return null;
}

/**
 * Get topic ID from content page slug (reverse mapping)
 */
export function getTopicIdFromSlug(slug: string): string | null {
  const entry = Object.entries(topicIdToSlugMap).find(([_, value]) => value === slug);
  return entry ? entry[0] : null;
}

